import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import type { ClientApprovalService } from 'src/app/shared/utils/client-first-access-invite.util';

export interface AuthResponse {
  BearerToken: string;
  id: number;
  name: string;
  email: string;
  user_type: 'user' | 'client';
  // Se user_type === 'user':
  role?: 'commercial' | 'supplier';
  is_admin?: boolean;
  // Se user_type === 'client':
  client_id?: number;
  brand_id?: number;
  /** Se true, este client user é admin da brand. */
  is_brand_admin?: boolean;
  /** Se true, é um "super cliente" global read-only (admin Armindohub). brand_id = null. */
  is_super_client?: boolean;
}

/** Normalised invite context for client first-access (in-app email, password pending). */
export interface ClientFirstAccessContext {
  email: string;
  name?: string | null;
  proto_id?: number | null;
  /** When set with {@link service_id}, prefer over legacy {@link proto_id}. */
  service?: ClientApprovalService | null;
  service_id?: number | null;
  /** Settings in-app invite: `/dashboard` (default). Backend may send `redirect_to` in GET context. */
  redirect_to?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  async login(postData: { email: string | null; password: string | null }) {
    // ajusta o URL se o teu backend estiver montado em /api
    const url = `${environment.url}/api/auth/signin`; 
    // se for /api/auth/signin mesmo, volta para:
    // const url = `${environment.url}/api/auth/signin`;

    const data = await firstValueFrom(this.http.post<AuthResponse>(url, postData));
    if (!data) {
      throw new Error('Empty auth response');
    }

    // guardar token + user
    localStorage.setItem('BearerToken', data.BearerToken);
    localStorage.setItem('user', JSON.stringify(data));

    return data;
  }

  /**
   * Removes the persisted session (token + cached user) WITHOUT navigating.
   *
   * Use this instead of `logout()` when you only want to wipe credentials and need to
   * preserve the current URL — typically when the login component itself runs on init
   * to clear leftover state. Calling `logout()` in that scenario silently strips the
   * `returnUrl` query param (because it navigates to `/authentication/login` with no
   * query), which breaks deep-links such as `/protos?openProto=<id>` after sign-in.
   */
  clearSession(): void {
    localStorage.removeItem('BearerToken');
    localStorage.removeItem('user');
  }

  /** Whether a non-empty bearer token is stored (not validated against the API). */
  hasBearerToken(): boolean {
    const t = localStorage.getItem('BearerToken');
    return typeof t === 'string' && t.trim().length > 0;
  }

  /** Parsed `user` from localStorage, if any. */
  getStoredUser(): AuthResponse | null {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw) as AuthResponse;
      return u && typeof u === 'object' ? u : null;
    } catch {
      return null;
    }
  }

  /**
   * True when there is a bearer and the cached user is a client with the same email
   * as the invite (case-insensitive). Used to skip first-access when the link is re-opened.
   */
  isClientSessionForEmail(email: string): boolean {
    if (!this.hasBearerToken()) return false;
    const u = this.getStoredUser();
    if (!u || u.user_type !== 'client') return false;
    const a = String(u.email ?? '').trim().toLowerCase();
    const b = String(email ?? '').trim().toLowerCase();
    return a.length > 0 && a === b;
  }

  logout() {
    this.clearSession();
    this.router.navigate(['/authentication/login']);
  }

  /**
   * Solicita reset de password por email
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const url = `${environment.url}/api/auth/forgot-password`;
    return await firstValueFrom(this.http.post<{ message: string }>(url, { email }));
  }

  /**
   * Valida token de reset e retorna informações do utilizador
   */
  async validateResetToken(token: string): Promise<{ email: string; name: string; user_type: 'user' | 'client' }> {
    const url = `${environment.url}/api/auth/reset-password/${token}`;
    return await firstValueFrom(this.http.get<{ email: string; name: string; user_type: 'user' | 'client' }>(url));
  }

  /**
   * Reseta a password usando token
   */
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const url = `${environment.url}/api/auth/reset-password`;
    return await firstValueFrom(this.http.post<{ message: string }>(url, { token, password: newPassword }));
  }

  /**
   * Validates the signed invite token and returns data to pre-fill the first-access form.
   * Backend: `GET /api/auth/client-first-access/context?token=...` (preferred; alias `invite_token`).
   */
  async getClientFirstAccessContext(inviteToken: string): Promise<ClientFirstAccessContext> {
    const url = `${environment.url}/api/auth/client-first-access/context`;
    const params = new HttpParams().set('token', inviteToken);
    const raw = await firstValueFrom(this.http.get<Record<string, unknown>>(url, { params }));
    const row = (raw && typeof raw === 'object' ? (raw as any).data ?? raw : {}) as Record<string, unknown>;
    const email = String(row['email'] ?? '').trim();
    if (!email) {
      throw new Error('Invalid invite response: missing email');
    }
    const protoRaw = row['proto_id'] ?? row['protoId'];
    const proto_id =
      typeof protoRaw === 'number' && Number.isFinite(protoRaw)
        ? protoRaw
        : protoRaw != null && String(protoRaw).trim() !== ''
          ? Number(protoRaw)
          : null;
    const serviceRaw = row['service'] ?? row['entity'];
    const serviceIdRaw = row['service_id'] ?? row['entity_id'] ?? row['resource_id'];
    let service: ClientApprovalService | null = null;
    if (typeof serviceRaw === 'string') {
      const s = serviceRaw.trim().toLowerCase();
      const allowed: ClientApprovalService[] = [
        'proto',
        'labdip',
        'bulk',
        'sourcing',
        'trim',
        'print_embroidery',
      ];
      if ((allowed as string[]).includes(s)) service = s as ClientApprovalService;
    }
    const sid =
      typeof serviceIdRaw === 'number' && Number.isFinite(serviceIdRaw)
        ? serviceIdRaw
        : serviceIdRaw != null && String(serviceIdRaw).trim() !== ''
          ? Number(serviceIdRaw)
          : null;
    const service_id = Number.isFinite(sid as number) ? (sid as number) : null;
    return {
      email,
      name: (row['name'] as string) ?? null,
      proto_id: Number.isFinite(proto_id as number) ? (proto_id as number) : null,
      service,
      service_id,
    };
  }

  /**
   * Sets the client password and returns the same session shape as `signin`.
   * Backend: `POST /api/auth/client-first-access` with JSON body (snake_case).
   * Accepts either `invite_token` or `token` (JWT from link) — backend should accept the same key as in the URL.
   */
  async completeClientFirstAccess(payload: {
    invite_token?: string;
    token?: string;
    password: string;
    /** If omitted, the same value as `password` is sent (UI may use a single field). */
    confirm_password?: string;
  }): Promise<AuthResponse> {
    const url = `${environment.url}/api/auth/client-first-access`;
    const confirm_password = payload.confirm_password ?? payload.password;
    const body =
      payload.token != null && payload.token !== ''
        ? { token: payload.token, password: payload.password, confirm_password }
        : {
            invite_token: payload.invite_token as string,
            password: payload.password,
            confirm_password,
          };
    const data = await firstValueFrom(this.http.post<AuthResponse>(url, body));
    if (!data?.BearerToken) {
      throw new Error('Empty auth response');
    }
    localStorage.setItem('BearerToken', data.BearerToken);
    localStorage.setItem('user', JSON.stringify(data));
    return data;
  }
}
